/**
 * Calendar Hooks - React hooks for business hours & calendar management
 * Epic 57: Business Hours & Calendar Management
 */

import {
  createHoliday,
  createOverride,
  deleteHoliday,
  deleteOverride,
  fetchBusinessHours,
  fetchHolidays,
  fetchMonthCalendar,
  fetchOverrides,
  resetBusinessHours,
  seedNationalHolidays,
  updateBusinessHoursDay,
  updateHoliday,
  updateOverride,
  type BusinessDayInfo,
  type BusinessHoursDay,
  type BusinessHoursOverride,
  type CreateHolidayInput,
  type CreateOverrideInput,
  type Holiday,
  type UpdateBusinessHoursDayInput,
  type UpdateHolidayInput,
  type UpdateOverrideInput,
} from '@/api/calendar';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UseBusinessHoursResult {
  businessHours: BusinessHoursDay[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseHolidaysResult {
  holidays: Holiday[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseOverridesResult {
  overrides: BusinessHoursOverride[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseMonthCalendarResult {
  calendar: BusinessDayInfo[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseCalendarMutationsResult {
  updateDay: (day: number, data: UpdateBusinessHoursDayInput) => Promise<BusinessHoursDay>;
  resetDefaults: () => Promise<BusinessHoursDay[]>;
  createHoliday: (data: CreateHolidayInput) => Promise<Holiday>;
  updateHoliday: (id: string, data: UpdateHolidayInput) => Promise<Holiday>;
  deleteHoliday: (id: string) => Promise<void>;
  seedNational: (year: number) => Promise<{ seeded: number }>;
  createOverride: (data: CreateOverrideInput) => Promise<BusinessHoursOverride>;
  updateOverride: (id: string, data: UpdateOverrideInput) => Promise<BusinessHoursOverride>;
  deleteOverride: (id: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// HOOKS
// ============================================

/**
 * Hook to fetch business hours (all 7 days)
 */
export function useBusinessHours(): UseBusinessHoursResult {
  const { t } = useTranslation();
  const [businessHours, setBusinessHours] = useState<BusinessHoursDay[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchBusinessHours();
      setBusinessHours(response);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.calendar.loadHours');
      setError(message);
      console.error('Error fetching business hours:', err);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return {
    businessHours,
    isLoading,
    error,
    refetch: fetch,
  };
}

/**
 * Hook to fetch holidays, optionally filtered by year
 */
export function useHolidays(year?: number): UseHolidaysResult {
  const { t } = useTranslation();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchHolidays(year);
      setHolidays(response);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.calendar.loadHolidays');
      setError(message);
      console.error('Error fetching holidays:', err);
    } finally {
      setIsLoading(false);
    }
  }, [year, t]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return {
    holidays,
    isLoading,
    error,
    refetch: fetch,
  };
}

/**
 * Hook to fetch business hours overrides, optionally filtered by date range
 */
export function useOverrides(from?: string, to?: string): UseOverridesResult {
  const { t } = useTranslation();
  const [overrides, setOverrides] = useState<BusinessHoursOverride[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchOverrides(from, to);
      setOverrides(response);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.calendar.loadOverrides');
      setError(message);
      console.error('Error fetching overrides:', err);
    } finally {
      setIsLoading(false);
    }
  }, [from, to, t]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return {
    overrides,
    isLoading,
    error,
    refetch: fetch,
  };
}

/**
 * Hook to fetch monthly calendar data
 */
export function useMonthCalendar(year: number, month: number): UseMonthCalendarResult {
  const { t } = useTranslation();
  const [calendar, setCalendar] = useState<BusinessDayInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchMonthCalendar(year, month);
      setCalendar(response);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.calendar.loadCalendar');
      setError(message);
      console.error('Error fetching month calendar:', err);
    } finally {
      setIsLoading(false);
    }
  }, [year, month, t]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return {
    calendar,
    isLoading,
    error,
    refetch: fetch,
  };
}

/**
 * Hook for calendar mutations (business hours, holidays, overrides)
 */
export function useCalendarMutations(): UseCalendarMutationsResult {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpdateDay = useCallback(
    async (day: number, data: UpdateBusinessHoursDayInput): Promise<BusinessHoursDay> => {
      setIsLoading(true);
      setError(null);
      try {
        return await updateBusinessHoursDay(day, data);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.calendar.updateHours');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const handleResetDefaults = useCallback(async (): Promise<BusinessHoursDay[]> => {
    setIsLoading(true);
    setError(null);
    try {
      return await resetBusinessHours();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('error.calendar.resetDefaults');
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const handleCreateHoliday = useCallback(
    async (data: CreateHolidayInput): Promise<Holiday> => {
      setIsLoading(true);
      setError(null);
      try {
        return await createHoliday(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.calendar.createHoliday');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const handleUpdateHoliday = useCallback(
    async (id: string, data: UpdateHolidayInput): Promise<Holiday> => {
      setIsLoading(true);
      setError(null);
      try {
        return await updateHoliday(id, data);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.calendar.updateHoliday');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const handleDeleteHoliday = useCallback(
    async (id: string): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        await deleteHoliday(id);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.calendar.deleteHoliday');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const handleSeedNational = useCallback(
    async (year: number): Promise<{ seeded: number }> => {
      setIsLoading(true);
      setError(null);
      try {
        return await seedNationalHolidays(year);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.calendar.seedNational');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const handleCreateOverride = useCallback(
    async (data: CreateOverrideInput): Promise<BusinessHoursOverride> => {
      setIsLoading(true);
      setError(null);
      try {
        return await createOverride(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.calendar.createOverride');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const handleUpdateOverride = useCallback(
    async (id: string, data: UpdateOverrideInput): Promise<BusinessHoursOverride> => {
      setIsLoading(true);
      setError(null);
      try {
        return await updateOverride(id, data);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.calendar.updateOverride');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const handleDeleteOverride = useCallback(
    async (id: string): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        await deleteOverride(id);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('error.calendar.deleteOverride');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  return {
    updateDay: handleUpdateDay,
    resetDefaults: handleResetDefaults,
    createHoliday: handleCreateHoliday,
    updateHoliday: handleUpdateHoliday,
    deleteHoliday: handleDeleteHoliday,
    seedNational: handleSeedNational,
    createOverride: handleCreateOverride,
    updateOverride: handleUpdateOverride,
    deleteOverride: handleDeleteOverride,
    isLoading,
    error,
  };
}
